import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { OrganizationService, OrganizationVM } from "@propertelligent/client-api";
import { UserLoginData } from "../../components/common/charts/Enums";

interface PlatformOrgContextValue {
  orgs: OrganizationVM[];
  selectedOrgId: number | null;
  selectOrg: (orgId: number) => void;
  loading: boolean;
}

const PlatformOrgContext = createContext<PlatformOrgContextValue>({
  orgs: [],
  selectedOrgId: null,
  selectOrg: () => {},
  loading: false,
});

export const usePlatformOrg = () => useContext(PlatformOrgContext);

/**
 * Holds the organization the Platform super-admin is currently "acting as" while impersonating the org portal.
 * The selected org id is written to sessionStorage under UserLoginData.organization_id — the single key every
 * reused organization page/hook reads to know which org it operates on — so the landlord pages work unchanged.
 * The consuming routed subtree is keyed by selectedOrgId so pages remount (and re-read the id) on a switch.
 */
export const PlatformOrgProvider = ({ children }: { children: React.ReactNode }) => {
  const [orgs, setOrgs] = useState<OrganizationVM[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const existing = sessionStorage.getItem(UserLoginData.organization_id);
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(
    existing && !Number.isNaN(parseInt(existing)) ? parseInt(existing) : null
  );

  const selectOrg = (orgId: number) => {
    sessionStorage.setItem(UserLoginData.organization_id, String(orgId));
    setSelectedOrgId(orgId);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await OrganizationService.getApiOrganization();
        const list: OrganizationVM[] = res?.success ?? [];
        if (cancelled) return;
        setOrgs(list);
        // Default to the first organization when none is selected yet.
        if ((selectedOrgId === null || selectedOrgId === 0) && list.length > 0) {
          selectOrg(Number(list[0].id));
        }
      } catch (error) {
        console.error("Error loading organizations for the platform org switcher:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Load once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({ orgs, selectedOrgId, selectOrg, loading }),
    [orgs, selectedOrgId, loading]
  );

  return <PlatformOrgContext.Provider value={value}>{children}</PlatformOrgContext.Provider>;
};

export default PlatformOrgContext;
